import React, { useState, useEffect, useMemo } from 'react';
import {
  FileText, Plus, X, Check, Loader2, AlertTriangle, Info,
  ChevronDown, ChevronRight, Layers, Target, CheckCircle,
  Flag, Clock, User, Copy, Trash2, Edit2, GripVertical,
  BookTemplate, Shield, Link, FileCheck, TrendingUp
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { normalizeRequirement } from '../utils/dataAdapters';

// ==========================================
// CONSTANTS
// ==========================================

const HIERARCHY_ICONS = {
  theme: Layers,
  epic: Target,
  requirement: FileText,
};

const HIERARCHY_COLORS = {
  theme: 'bg-purple-100 text-purple-700 border-purple-200',
  epic: 'bg-blue-100 text-blue-700 border-blue-200',
  requirement: 'bg-green-100 text-green-700 border-green-200',
};

const CATEGORY_COLORS = {
  'Functional': 'bg-blue-100 text-blue-700',
  'Non-Functional': 'bg-purple-100 text-purple-700',
  'Integration': 'bg-cyan-100 text-cyan-700',
  'Reporting': 'bg-amber-100 text-amber-700',
  'Compliance': 'bg-red-100 text-red-700',
  'UX': 'bg-pink-100 text-pink-700',
  'Technical': 'bg-gray-100 text-gray-700',
};

const PRIORITY_OPTIONS = ['Must Have', 'Should Have', 'Could Have', "Won't Have"];
const AC_TYPES = ['gherkin', 'scenario', 'performance', 'data_validation'];
const RISK_CATEGORIES = ['Delivery', 'Technical', 'Resourcing', 'Dependency', 'Data'];
const CONSTRAINT_CATEGORIES = ['Delivery', 'Technical', 'Architecture', 'Vendor/Tooling'];

// ==========================================
// QUALITY BADGE COMPONENT
// ==========================================

export const QualityBadge = ({ flags, showScore = true }) => {
  if (!flags) return null;
  
  const score = flags.score ?? 100;
  const issues = [];
  
  if (flags.missing_ac) issues.push('Missing AC');
  if (flags.missing_category) issues.push('No Category');
  if (flags.missing_dependencies) issues.push('Check Dependencies');
  if (flags.ambiguous_language) issues.push('Ambiguous');
  if (flags.incomplete_fields) issues.push('Incomplete');
  
  const colorClass = score >= 80 
    ? 'bg-green-100 text-green-700' 
    : score >= 60 
      ? 'bg-amber-100 text-amber-700' 
      : 'bg-red-100 text-red-700';
  
  return (
    <div className="flex items-center gap-2">
      {showScore && (
        <span className={`text-xs px-2 py-0.5 rounded ${colorClass}`}>
          {score}%
        </span>
      )}
      {issues.length > 0 && (
        <div className="flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
          <span className="text-xs text-amber-600">{issues.join(', ')}</span>
        </div>
      )}
    </div>
  );
};

// ==========================================
// TEMPLATE SELECTOR COMPONENT
// ==========================================

const TemplateSelector = ({
  tenantId,
  templateType,
  selectedTemplateId,
  onSelect,
  onApply,
  disabled = false
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen && tenantId) {
      loadTemplates();
    }
  }, [isOpen, tenantId, templateType]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.getMergedTemplatesByType(templateType, tenantId);
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to load templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedTemplateId);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-50 w-full justify-between"
        data-testid="template-selector-btn"
      >
        <div className="flex items-center gap-2">
          <BookTemplate className="w-4 h-4 text-gray-500" />
          <span className="truncate">
            {selectedTemplate?.name || 'Select Template (Optional)'}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center">
                <Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" />
              </div>
            ) : templates.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500">
                No templates available
              </div>
            ) : (
              <>
                <button
                  onClick={() => {
                    onSelect(null);
                    setIsOpen(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-500"
                >
                  None (Blank Requirement)
                </button>
                {templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => {
                      onSelect(template.id);
                      if (onApply) onApply(template);
                      setIsOpen(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                      template.id === selectedTemplateId ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium">{template.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        template.source === 'tenant' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {template.source}
                      </span>
                    </div>
                    {template.description && (
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{template.description}</p>
                    )}
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ==========================================
// ACCEPTANCE CRITERIA EDITOR
// ==========================================

const AcceptanceCriteriaEditor = ({ criteria, onChange, disabled = false }) => {
  const [newCriterion, setNewCriterion] = useState({
    content: '',
    type: 'scenario',
    given: '',
    when: '',
    then: '',
  });

  const handleAdd = () => {
    if (!newCriterion.content && !newCriterion.given) return;
    
    const ac = {
      id: `ac_${Date.now()}`,
      ...newCriterion,
      is_met: false,
    };
    
    onChange([...criteria, ac]);
    setNewCriterion({ content: '', type: 'scenario', given: '', when: '', then: '' });
  };

  const handleRemove = (index) => {
    onChange(criteria.filter((_, i) => i !== index));
  };

  const handleToggleMet = (index) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], is_met: !updated[index].is_met };
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          Acceptance Criteria
        </label>
        <span className="text-xs text-gray-500">{criteria.length} criteria</span>
      </div>

      {/* Existing criteria */}
      {criteria.length > 0 && (
        <div className="space-y-2">
          {criteria.map((ac, index) => (
            <div key={ac.id || index} className={`p-3 rounded-lg border ${
              ac.is_met ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-start gap-2">
                <button
                  type="button"
                  onClick={() => handleToggleMet(index)}
                  disabled={disabled}
                  className="mt-0.5"
                >
                  <CheckCircle className={`w-4 h-4 ${
                    ac.is_met ? 'text-green-600' : 'text-gray-400'
                  }`} />
                </button>
                <div className="flex-1 min-w-0">
                  {ac.type === 'gherkin' ? (
                    <div className="text-sm space-y-1">
                      <div><span className="font-medium text-purple-600">Given</span> {ac.given}</div>
                      <div><span className="font-medium text-blue-600">When</span> {ac.when}</div>
                      <div><span className="font-medium text-green-600">Then</span> {ac.then}</div>
                    </div>
                  ) : (
                    <p className="text-sm">{ac.content}</p>
                  )}
                </div>
                <span className="text-xs px-1.5 py-0.5 bg-gray-200 rounded">
                  {ac.type}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="p-1 hover:bg-red-100 rounded"
                  >
                    <X className="w-3.5 h-3.5 text-red-500" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new criterion */}
      {!disabled && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 space-y-2">
          <div className="flex items-center gap-2">
            <select
              value={newCriterion.type}
              onChange={(e) => setNewCriterion(prev => ({ ...prev, type: e.target.value }))}
              className="px-2 py-1 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-400"
            >
              {AC_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          {newCriterion.type === 'gherkin' ? (
            <div className="space-y-2">
              <input
                type="text"
                value={newCriterion.given}
                onChange={(e) => setNewCriterion(prev => ({ ...prev, given: e.target.value }))}
                placeholder="Given..."
                className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="text"
                value={newCriterion.when}
                onChange={(e) => setNewCriterion(prev => ({ ...prev, when: e.target.value }))}
                placeholder="When..."
                className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-400"
              />
              <input
                type="text"
                value={newCriterion.then}
                onChange={(e) => setNewCriterion(prev => ({ ...prev, then: e.target.value }))}
                placeholder="Then..."
                className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-400"
              />
            </div>
          ) : (
            <textarea
              value={newCriterion.content}
              onChange={(e) => setNewCriterion(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Describe the acceptance criterion..."
              className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:ring-1 focus:ring-blue-400 resize-none"
              rows={2}
            />
          )}
          
          <button
            type="button"
            onClick={handleAdd}
            className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Criterion
          </button>
        </div>
      )}
    </div>
  );
};

// ==========================================
// MAIN REQUIREMENT EDITOR MODAL
// ==========================================

const RequirementEditorModal = ({
  isOpen,
  onClose,
  requirement,
  projectId,
  tenantId,
  userId,
  categories,
  hierarchyTypes,
  parentOptions = [],
  onSave,
  isSaving = false,
  defaultHierarchyType = 'requirement'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    hierarchy_type: defaultHierarchyType,
    category: '',
    parent_id: null,
    template_id: null,
    template_data: {},
    acceptance_criteria: [],
    benefits: [],
    risks: [],
    constraints: [],
    dependencies: [],
    priority: '',
    owner: '',
    estimated_effort: '',
    labels: [],
    order: 0,
  });

  useEffect(() => {
    if (requirement) {
      const normalized = normalizeRequirement(requirement);
      setFormData({
        name: normalized.name,
        description: normalized.description,
        hierarchy_type: normalized.hierarchy_type,
        category: normalized.category || '',
        parent_id: normalized.parent_id,
        template_id: normalized.template_id,
        template_data: normalized.template_data,
        acceptance_criteria: normalized.acceptance_criteria,
        benefits: normalized.benefits || [],
        risks: normalized.risks,
        constraints: normalized.constraints,
        dependencies: normalized.dependencies,
        priority: normalized.priority || '',
        owner: normalized.owner || '',
        estimated_effort: normalized.estimated_effort || '',
        labels: normalized.labels,
        order: normalized.order,
      });
    } else {
      // Reset form with the correct default hierarchy type
      setFormData({
        name: '',
        description: '',
        hierarchy_type: defaultHierarchyType,
        category: '',
        parent_id: null,
        template_id: null,
        template_data: {},
        acceptance_criteria: [],
        benefits: [],
        risks: [],
        constraints: [],
        dependencies: [],
        priority: '',
        owner: '',
        estimated_effort: '',
        labels: [],
        order: 0,
      });
    }
  }, [requirement, isOpen, defaultHierarchyType]);

  const handleApplyTemplate = (template) => {
    if (!template) return;
    
    // Apply template's default values
    const defaults = template.default_values || {};
    setFormData(prev => ({
      ...prev,
      template_data: { ...prev.template_data, ...defaults },
    }));
    
    toast.success(`Applied template: ${template.name}`);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    onSave(formData);
  };

  const TypeIcon = HIERARCHY_ICONS[formData.hierarchy_type] || FileText;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={e => e.stopPropagation()}
          data-testid="requirement-editor-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className={`p-2 rounded-lg ${HIERARCHY_COLORS[formData.hierarchy_type]}`}>
                <TypeIcon className="w-5 h-5" />
              </span>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {requirement ? 'Edit' : 'Create'} {formData.hierarchy_type.charAt(0).toUpperCase() + formData.hierarchy_type.slice(1)}
                </h3>
                <p className="text-xs text-gray-500">
                  {requirement ? 'Update details below' : 'Fill in the details below'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Type & Category Row */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                <select
                  value={formData.hierarchy_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, hierarchy_type: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  disabled={!!requirement}
                  data-testid="hierarchy-type-select"
                >
                  {Object.keys(hierarchyTypes || {}).map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  data-testid="category-select"
                >
                  <option value="">Select Category...</option>
                  {(categories || []).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  data-testid="priority-select"
                >
                  <option value="">Select Priority...</option>
                  {PRIORITY_OPTIONS.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Template Selector */}
            {formData.hierarchy_type === 'requirement' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Use Template</label>
                <TemplateSelector
                  tenantId={tenantId}
                  templateType="REQUIREMENT"
                  selectedTemplateId={formData.template_id}
                  onSelect={(id) => setFormData(prev => ({ ...prev, template_id: id }))}
                  onApply={handleApplyTemplate}
                />
              </div>
            )}

            {/* Parent Selection */}
            {formData.hierarchy_type !== 'theme' && parentOptions.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Parent</label>
                <select
                  value={formData.parent_id || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, parent_id: e.target.value || null }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  data-testid="parent-select"
                >
                  <option value="">No Parent (Root Level)</option>
                  {parentOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>
                      {opt.hierarchy_type}: {opt.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                placeholder="Enter requirement name..."
                required
                data-testid="requirement-name-input"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none resize-none"
                rows={3}
                placeholder="Describe the requirement in detail..."
                data-testid="requirement-description-input"
              />
            </div>

            {/* Acceptance Criteria (for requirements only) */}
            {formData.hierarchy_type === 'requirement' && (
              <AcceptanceCriteriaEditor
                criteria={formData.acceptance_criteria}
                onChange={(ac) => setFormData(prev => ({ ...prev, acceptance_criteria: ac }))}
              />
            )}
            
            {/* Expected Benefits Section */}
            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  Expected Benefits
                </label>
                <span className="text-xs text-gray-500">
                  {(formData.benefits || []).length} benefit(s)
                </span>
              </div>
              
              {/* Existing Benefits */}
              {(formData.benefits || []).length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.benefits.map((benefit, index) => (
                    <div key={benefit.id || index} className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              benefit.type === 'financial' ? 'bg-green-100 text-green-700' :
                              benefit.type === 'operational' ? 'bg-blue-100 text-blue-700' :
                              benefit.type === 'strategic' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {benefit.type || 'other'}
                            </span>
                            {benefit.annual_value > 0 && (
                              <span className="text-xs font-medium text-green-700">
                                ${benefit.annual_value?.toLocaleString()}/year
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-800">{benefit.description}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const updated = [...formData.benefits];
                            updated.splice(index, 1);
                            setFormData(prev => ({ ...prev, benefits: updated }));
                          }}
                          className="p-1 hover:bg-red-100 rounded"
                        >
                          <X className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Benefit */}
              <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Benefit Type</label>
                    <select
                      id="new-benefit-type"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      defaultValue="financial"
                    >
                      <option value="financial">Financial</option>
                      <option value="operational">Operational</option>
                      <option value="strategic">Strategic</option>
                      <option value="compliance">Compliance</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Annual Value ($)</label>
                    <input
                      id="new-benefit-value"
                      type="number"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Description</label>
                  <input
                    id="new-benefit-description"
                    type="text"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Describe the expected benefit..."
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const typeEl = document.getElementById('new-benefit-type');
                    const valueEl = document.getElementById('new-benefit-value');
                    const descEl = document.getElementById('new-benefit-description');
                    
                    if (descEl.value.trim()) {
                      const newBenefit = {
                        id: `ben_${Date.now()}`,
                        type: typeEl.value,
                        annual_value: parseFloat(valueEl.value) || 0,
                        description: descEl.value.trim(),
                        confidence: 'medium'
                      };
                      setFormData(prev => ({
                        ...prev,
                        benefits: [...(prev.benefits || []), newBenefit]
                      }));
                      // Clear inputs
                      valueEl.value = '';
                      descEl.value = '';
                    }
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Benefit
                </button>
              </div>
              
              <p className="text-xs text-gray-500 mt-2">
                Benefits added here will sync with the Financial ROI section when the scope is baselined.
              </p>
            </div>

            {/* Additional fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner</label>
                <input
                  type="text"
                  value={formData.owner}
                  onChange={(e) => setFormData(prev => ({ ...prev, owner: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  placeholder="Owner name..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Estimated Effort (hours)</label>
                <input
                  type="number"
                  value={formData.estimated_effort}
                  onChange={(e) => setFormData(prev => ({ ...prev, estimated_effort: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
          </form>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSaving}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              data-testid="save-requirement-btn"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              {isSaving ? 'Saving...' : requirement ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RequirementEditorModal;
export { TemplateSelector, AcceptanceCriteriaEditor };
